import type { MathRun } from "../Shared/Pdf/mathRuns.ts";

export interface SelectOption {
  readonly value: Operations;
  readonly sign: string;
}

export interface DigitNumbers {
Range: number[];
LowRange: number[];
HighRange: number[];
}

/**
 * A fraction as it is written on the page. `whole` carries the integer part of
 * a mixed number, so 7/4 is rendered as 1 3/4 rather than left improper; a
 * numerator of 0 means the value came out as a whole number.
 */
export interface Fraction {
  whole?: number;
  numerator: number;
  denominator: number;
}

/**
 * A fraction with its sign held apart from its parts (ADR 0122 §5). `Fraction`
 * has no sign, and a sign on `whole` would be lost when `whole` is 0. As an
 * answer it is the mixed number's sign: `-1 1/4` is `negative` with `whole` 1.
 */
export interface SignedFraction extends Fraction {
  negative: boolean;
}

/**
 * One signed-fraction problem on the Integers format: two signed proper
 * fractions, the operator between them, and the signed answer in lowest terms.
 * The glyph the sheet draws, so the key and the question cannot disagree.
 */
export interface RationalFractionProblem {
  operands: [SignedFraction, SignedFraction];
  operator: '+' | '-' | '×' | '÷';
  answer: SignedFraction;
}

/** One row of a riddle's decoder: an exact answer and the letter it stands for. */
export interface RiddleDecoderEntry {
  answer: number;
  letter: string;
}

export interface RiddleSheet {
  question: string;
  /** The punchline, A to Z and spaces. */
  punchline: string;
  /** Every letter the punchline uses plus decoys, ascending by answer. */
  decoder: RiddleDecoderEntry[];
}

export interface MagicSquareSpec {
  /** Row by row, left to right. */
  cells: number[];
  /** Indices into `cells` that are printed; the rest are blank. */
  given: number[];
}

export interface ProblemData
{
key: number,
numbers: number[],
operator: string,
/** Present only on fraction problems: [left operand, right operand, answer]. */
fractions?: Fraction[],
/**
 * Pre-formatted text, for problems whose numbers are not written the way they
 * are stored. What each entry means is the renderer's business: the vertical
 * layout reads [top, bottom, answer] - money is held in whole cents so the
 * arithmetic stays exact, and printed as $3.45 - place value reads
 * [prompt, answer], and telling time reads [question type, digital time].
 */
display?: string[],
/**
 * Which operand is left blank on a missing-number sheet: 0 or 1, absent
 * otherwise. Set after generation from the MissingNumber setting; the answer
 * (numbers[2]) is never hidden. Its own field, not a fourth `numbers` entry -
 * that slot is the long division remainder. See ADR 0005.
 */
hidden?: 0 | 1,
/**
 * Which story a Varied word problem was told as (ADR 0073): a story type id, or
 * "ordinary" for the operation's existing pool. Absent on every Simple sheet.
 * Not rendered - it exists so a sheet can be checked for what it holds.
 */
storyType?: string,
/**
 * Decimal places the problem was built at, on decimal problems only.
 *
 * Deliberately its own field rather than a fourth entry in `numbers`: that
 * slot is the long division remainder, and a place count sitting in it reads
 * as a remainder of 1 to 3 on every problem. See ADR 0004.
 */
decimalPlaces?: number,
/**
 * On a color-by-number sheet only: which cell of the picture this problem
 * fills, and the colour its answer keys to (ADR 0058 §3). Its own field -
 * never `numbers[3]`, which is the long division remainder.
 */
colorCell?: { index: number, color: string },
/**
 * A riddle-decoder problem (ADR 0088 §3): which blank of the punchline it
 * fills and the letter its answer decodes to. Its own field, never `numbers[3]`.
 */
riddle?: { position: number, letter: string },
/**
 * What the whole riddle page prints besides its problems: the question, the
 * punchline and the decoder. Carried by the page's first problem only.
 */
riddleSheet?: RiddleSheet,
/**
 * A magic square (ADR 0088 §4): the nine cells in reading order and which of
 * them are printed. `numbers` holds the total.
 */
magicSquare?: MagicSquareSpec,
/**
 * Which kind of rounding/comparing question this is, so the renderer lays a
 * full-width rounding sentence out one to a row and a short comparison three
 * to a row. Present only on that format. See ADR 0006.
 */
roundingComparingKind?: 'rounding' | 'comparing',
/**
 * What to draw when the sheet's RoundingDisplay is NumberLine (ADR 0046).
 * Present only when this problem's rounding place (or, for comparing, the
 * span between the pair) is precise enough to land on a tick - absent
 * problems fall back to the plain sentence, keeping the same question. Its
 * own field, never a fourth `numbers` entry - that slot is the long division
 * remainder.
 */
roundingLine?: RoundingNumberLineSpec,
/**
 * A counting-coins or making-change problem. Present only on those money
 * question types. Its own field, never a fourth `numbers` entry. See ADR 0018.
 */
coins?: CoinProblem,
/**
 * True when `display` holds a story rather than a vertical problem -
 * `[story, question, worked answer]` - so the page lays it out with the
 * word-problem renderer whatever the sheet's format. Set by money word
 * problems (ADR 0063); the Word Problems format itself does not need it.
 */
wordProblem?: boolean,
/**
 * An elapsed-time problem: two times, how they are shown, which value is the
 * blank, and the answer in words. Present only on the elapsed question types.
 * Its own field, never a fourth `numbers` entry. See ADR 0017.
 */
elapsed?: ElapsedTime,
/**
 * An A.M.-or-P.M. problem (ADR 0090 §1): an event, a printed time, and the
 * two halves of the day to circle. Its own field, never a fourth `numbers`
 * entry.
 */
amPm?: AmPmProblem,
/**
 * An order-of-operations problem is a tree, not a pair. The written expression
 * and the answer travel in `display` as [expression, answer]; `numbers` holds
 * the operands in the order they appear so the arithmetic stays inspectable,
 * and `operator` is empty. This flag marks the format for the renderer. See
 * ADR 0019.
 */
isExpression?: boolean,
/**
 * A fact-family / number-bond problem: the three related numbers laid out as a
 * group. Present only on that format. Its own field, never `numbers[3]`. See ADR 0020.
 */
factFamily?: FactFamily,
/**
 * Which problems share one rendered lead-in, and what that lead-in is. The
 * page groups on `key` and renders whichever lead-in the tag carries above
 * the group's rows (ADR 0040 §1) - a spiral-review section's heading, or a
 * bar graph's shared chart. One mechanism, not two: spiral review
 * (ADR 0013) was first and is unchanged in behaviour, only in field name -
 * `spiralSection` is superseded by this, generalised for a second lead-in
 * shape rather than left as spiral review's private arrangement that bar
 * graphs would otherwise have to copy.
 */
problemGroup?: ProblemGroup,
/**
 * Which fraction sub-skill this problem is, when the Fractions format is not
 * doing arithmetic (simplify, compare, to a decimal, …). The renderer lays each
 * out differently. `display[0]` carries a text answer where the answer is not a
 * fraction (a comparison symbol, a decimal). See ADR 0021.
 */
fractionQuestion?: FractionQuestionOptions,
/** Fraction-of-a-number: the whole number the fraction is taken of. */
fractionOfNumber?: number,
/**
 * Which shape an algebra prompt is, so the renderer places the blank without
 * having to infer it from the string (ADR 0028).
 */
algebraQuestion?: AlgebraQuestionOptions,
/**
 * What a Writing Expressions prompt was built from (ADR 0123 §4): the shape
 * (`S1`-`S6`, `T1`-`T6`), the index of the phrasing in that language's bank, and
 * the numbers it uses (0 when the shape has no use for one). The de-duplication
 * fingerprint reads this, since the printed phrase is words and the letter is
 * not part of the question. Its own field, never `numbers`.
 */
expressionWords?: { shape: string, phrasing: number, a: number, b: number },
/**
 * A Systems of Equations problem (ADR 0139 §5): the two equations as their left
 * and right sides, so the renderer can stack them on the equals sign, the
 * solution, the kind of system it was built as, and the sheet's pair separator
 * (`, ` or ` ; `, since a French comma is the decimal separator). Its own field,
 * never `numbers[3]`; `display` carries the one-line form and the pair.
 */
system?: {
  equations: [[string, string], [string, string]],
  x: number,
  y: number,
  kind: 'substitution' | 'elimination' | 'multiplyFirst',
  separator: string,
},
/**
 * Which kind of integer question this is, so the renderer places the blank
 * without parsing the prompt back apart (ADR 0030).
 */
integerQuestion?: IntegerQuestionOptions,
/**
 * A signed-fraction problem of the rational question types (ADR 0122 §5): the
 * operands, operator and answer the renderer stacks. Its own field, never
 * `numbers[3]`. `display` carries the same problem as inline text
 * (`-3/4 + (-1/2)`) for the places that read text.
 */
rationalFractions?: RationalFractionProblem,
/** Which number-property question this is (ADR 0032). */
numberPropertyQuestion?: NumberPropertyOptions,
/**
 * A prompt that prints notation a `Text` cannot hold (ADR 0132): the runs
 * `MathText` draws. `display[0]` is the same prompt as one Unicode string
 * (`mathPlain`), for the de-duplication fingerprint and the screens.
 */
mathPrompt?: MathRun[],
/**
 * A key that prints notation a `Text` cannot hold (ADR 0130): the runs
 * `MathText` draws at the key's size in place of `display[1]`, which is the same
 * answer as one Unicode string (`mathPlain`).
 */
mathAnswer?: MathRun[],
/** What a scientific-notation problem was built from (ADR 0130), for the tests and the copy audit. */
scientific?: ScientificProblem,
/** What an exponent-rules problem was built from (ADR 0136), for the tests and the copy audit. */
exponentRule?: ExponentRuleProblem,
/** Which unit system a conversion came from (ADR 0032). */
conversionQuestion?: ConversionQuestionOptions,
/** The unit the conversion's answer blank is followed by. */
conversionUnit?: string,
/** Which kind of sequence this is (ADR 0032 §5). */
patternQuestion?: PatternQuestionOptions,
/** Which number system a number line asks about (ADR 0033). */
numberLineQuestion?: NumberLineQuestionOptions,
/** What line to draw. Its own field - a drawing has more than three values. */
numberLine?: NumberLineSpec,
/** Which percent question this is (ADR 0034 §2). */
percentQuestion?: PercentQuestionOptions,
/** Which situation a money percent question is set in (ADR 0120 §1). */
percentContext?: PercentContext,
/** Which ratio question this is (ADR 0035 §2). */
ratioQuestion?: RatioQuestionOptions,
/**
 * The table a Proportional Tables problem draws (ADR 0104 §4): a row of x
 * values over a row of y values, `null` for a blank the student fills in. The
 * answers stay in `display`.
 */
ratioTable?: { x: (number | null)[]; y: (number | null)[] },
/**
 * The graph a Proportional Graphs problem draws (ADR 0105 §4): the value of one
 * y gridline, the line y = slope * x + intercept across a first-quadrant grid of
 * x from 0 to 10, and the points dotted where it crosses gridlines. The answers
 * stay in `display`.
 */
ratioGraph?: { yStep: number; slope: number; intercept: number; dots: { x: number; y: number }[] },
/**
 * The graph a Slope from a Graph problem draws (ADR 0126 §3): a grid from `min`
 * to `max` on both axes, the line y = (p / q) x + intercept, and two marked
 * points on grid crossings. `intercept` may be a fraction of a unit. The key stays
 * in `display`.
 */
slopeGraph?: { min: number; max: number; slope: { p: number; q: number }; intercept: number; dots: { x: number; y: number }[] },
/** Which shape question this is (ADR 0038 §1). */
shapeQuestion?: ShapeQuestionOptions,
/** The rectangle to draw, and which of its sides carry labels. */
rectangle?: RectangleSpec,
/** The parallelogram, trapezoid or triangle to draw (ADR 0121 §2). Its area stays in `display`. */
areaFigure?: AreaFigureSpec,
/**
 * A Scale Drawing problem's own data (ADR 0107): the drawing's sides in cm, the
 * scale in metres per cm (printed as "Scale: 1 cm = {scale} m"), which side the
 * drawing hides for Drawing length, and the second scale of Another scale. The
 * drawn rectangle rides on `rectangle`; the answers stay in `display`.
 */
scaleDrawing?: ScaleDrawingSpec,
/** Which volume question this is (ADR 0095 §3). */
volumeQuestion?: VolumeQuestionOptions,
/** The box to draw, and which of its edges carry labels (ADR 0095 §2). */
box?: BoxSpec,
/** The two boxes of a "Two boxes" question, all five edges labelled (ADR 0097). */
joinedBoxes?: JoinedBoxesSpec,
/** The box a Surface Area problem draws as a flat net, labelled once per edge (ADR 0100). */
boxNet?: BoxSpec,
/** The cylinder, cone or sphere a Volume problem draws (ADR 0140). */
solid?: SolidSpec,
/** Which circle question this is (ADR 0106 §3). */
circleQuestion?: CircleQuestionOptions,
/**
 * The circle to draw (ADR 0106 §2): whether the labelled segment is a radius or
 * a diameter, and its length in cm. `null` is a diameter labelled `?`, the
 * Diameter from Circumference question. The answers stay in `display`.
 */
circle?: CircleSpec,
/**
 * Which probability question this is (ADR 0108 §2): never `Mixed`, which only
 * deals the other three.
 */
probabilityQuestion?: ProbabilityQuestionOptions,
/**
 * What a probability problem draws (ADR 0108 §1) and what it counted. The
 * answer itself stays in `display`.
 */
probability?: ProbabilityData,
/**
 * Which Pythagorean question this is (ADR 0109 §2): never `Mixed`, which only
 * deals the other three.
 */
pythagoreanQuestion?: PythagoreanQuestionOptions,
/**
 * The right triangle to draw (ADR 0109 §3), for the two questions that draw
 * one. The answers stay in `display`; the converse draws nothing.
 */
rightTriangle?: RightTriangleSpec,
/** The triple a Pythagorean problem was built from, and for the converse what it printed (ADR 0109 §4). */
pythagorean?: PythagoreanData,
/**
 * Which averages question this is (ADR 0039 §2). The list itself travels in
 * `display[0]` as part of the written prompt - there is nothing else to pull
 * out, unlike a rectangle or a number line, which is why this format needs no
 * sibling field the way `rectangle` and `numberLine` do.
 */
averageQuestion?: AverageQuestionOptions,
/** Which bar-graph question this is (ADR 0039 §3). */
barGraphQuestion?: BarGraphQuestionOptions,
/**
 * Which line-plot question this is (ADR 0084 §5). The plot itself is on the
 * problem's `problemGroup`, drawn once for the group like a bar chart.
 */
linePlotQuestion?: LinePlotQuestionOptions,
/**
 * Which histogram question this is (ADR 0099 §2). The histogram itself is on
 * the problem's `problemGroup`, drawn once for the group like a bar chart.
 */
histogramQuestion?: HistogramQuestionOptions,
/**
 * Which box-plot question this is (ADR 0099 §3). The plot itself is on the
 * problem's `problemGroup`, drawn once for the group like a line plot.
 */
boxPlotQuestion?: BoxPlotQuestionOptions,
/**
 * Which coordinate-plane question this is (ADR 0085 §2). The grid itself is on
 * the problem's `problemGroup`, drawn once beside the group's questions.
 */
coordinateQuestion?: CoordinateQuestionOptions,
/**
 * Which calendar question this is (ADR 0093 §4). The month itself is on the
 * problem's `problemGroup`, drawn once above the page's questions.
 */
calendarQuestion?: CalendarQuestionOptions,
/**
 * Which geometry question this is (ADR 0049 §1). Like Averages, the figure
 * travels inside `display[0]` as words - this format deliberately draws
 * nothing (ADR 0049 §2), so there is no sibling spec field the way
 * `rectangle` and `numberLine` have one.
 */
geometryQuestion?: GeometryQuestionOptions,
}

/** Before or after noon. */
export type DayHalf = 'am' | 'pm';

/** What a crossing-noon sheet prints for each half, and for 12:00 (ADR 0090 §4, §5). */
export interface DayHalfLabels {
  am: string;
  pm: string;
  /** The label on a number line's 12:00 tick. */
  noon: string;
}

export interface AmPmProblem {
  /** "Ava eats breakfast at 7:30." - the time is printed without its half. */
  sentence: string;
  time: { hours: number; minutes: number };
  /** The answer. */
  half: DayHalf;
  /** The two choices, in the order printed: a.m. first. */
  labels: DayHalfLabels;
}

export interface ElapsedTime {
  mode: TimeDisplayOptions;
  /** `half` is present only on a crossing-noon sheet (ADR 0090 §3). */
  start: { hours: number; minutes: number; half?: DayHalf };
  end: { hours: number; minutes: number; half?: DayHalf };
  /**
   * The labels each time's half prints with. Present exactly when the sheet
   * crosses noon; absent, nothing prints a half (ADR 0017 §2).
   */
  halves?: DayHalfLabels;
  /** The duration in words, e.g. "1 hour 35 minutes". */
  durationText: string;
  /** Which of the three values the student works out. */
  asked: 'duration' | 'end' | 'start';
  /** The answer, as it should read on the key. */
  answerText: string;
  /** A word-problem sentence, when the display mode is `Sentence`. */
  sentenceText?: string;
  /**
   * The chrome Problems-UI/Time.tsx prints around the times themselves - the
   * number-line prompt line, the digital-display prompt line, the "after"
   * connector and the "Answer:" label - precomputed here rather than in the
   * renderer, in the worksheet's printed language (ADR 0051 §7). Following
   * the WordProblemGenerator precedent: text is filled in beside the
   * generator, never in a renderer that draws the same data twice (once for
   * the worksheet, once for the key) and could disagree with itself.
   */
  chrome: {
    /** e.g. "What time did it end?" - keyed by `asked` in NUMBER_LINE_PROMPTS. */
    numberLinePrompt: string;
    /** e.g. "How much time passed? ____________" - the two-clock/digital prompt line. */
    digitalPrompt: string;
    /** e.g. "after" - the word before the blank in the two-clock/digital connector. */
    afterWord: string;
    /** e.g. "Answer:" */
    answerLabel: string;
  };
  /**
   * What to draw when the display mode is `NumberLine` (ADR 0044). `from` and
   * `to` are the start and end in minutes past the sweep's start - the same
   * units `start`/`end` convert to - so the renderer never re-derives them.
   * `marks` is only the times the worksheet page should mark: the asked-for
   * value must never appear here (that would print the answer). The answer
   * key marks both `from` and `to` regardless of what `marks` holds.
   */
  line?: ElapsedNumberLineSpec;
}

/** What `NumberLine.tsx` needs to draw an elapsed-time span (ADR 0044 §2). */
export interface ElapsedNumberLineSpec {
  from: number;
  to: number;
  intervals: number;
  labelEvery: number;
  /** The worksheet-page marks - never includes the value being asked for. */
  marks: number[];
}

/**
 * What `NumberLine.tsx` needs to draw a rounding or comparing line (ADR 0046).
 *
 * Rounding: `from`/`to` are the two rounding candidates - both printed on the
 * axis, per ADR 0046 §3, since printing both candidates is not printing the
 * answer. `marks` holds only the number being rounded, never the answer;
 * `answer` is the correct candidate, added to `marks` on the key only
 * (ADR 0040 §5's precedent for a key redrawing with the answer filled in).
 *
 * Comparing: `from`/`to` pad the span between the two compared values, and
 * `marks` holds both of them, worksheet and key alike - the answer is a
 * relation, not a value either mark could reveal, so nothing here needs to be
 * withheld. `answer` is unused.
 *
 * `places` is the decimal places a tick's value is written at - the finest
 * place the pair or the rounded number is held to, 0 for whole numbers.
 */
export interface RoundingNumberLineSpec {
  from: number;
  to: number;
  intervals: number;
  labelEvery: number;
  marks: number[];
  answer?: number;
  places: number;
}

export const enum Operations {
Addition = "Addition",
Subtraction = "Subtraction",
Multiplication = "Multiplication",
Division = "Division",
LongDivision = "Long Division",
}

export const OperationOptions: SelectOption[] = [
{ sign: '+', value: Operations.Addition},
{ sign: '-', value: Operations.Subtraction},
{ sign: 'x', value: Operations.Multiplication},
{ sign: '÷', value: Operations.Division},
{ sign: '÷', value: Operations.LongDivision},
];

export const SingleDigitNumbers = {
Range: [0,1,2,3,4,5,6,7,8,9],
LowRange: [0,1,2,3,4],
HighRange: [5,6,7,8,9]
};
export const DoubleDigitNumbers = {
Range: [10,20,30,40,50,60,70,80,90],
LowRange: [10,20,30,40],
HighRange: [50,60,70,80,90]
}

export const TripleDigitNumbers = {
Range: [100,200,300,400,500,600,700,800,900],
LowRange: [100,200,300,400],
HighRange: [500,600,700,800,900]
}

/**
 * The one-digit range on a multiplication or division sheet: the facts to
 * 12 x 12. Chosen over SingleDigitNumbers by `bucketFor` in FactRanges.ts,
 * never directly. See ADR 0064.
 */
export const TimesTableNumbers = {
Range: [0,1,2,3,4,5,6,7,8,9,10,11,12],
LowRange: [0,1,2,3,4,5,6],
HighRange: [7,8,9,10,11,12]
}

export enum RegroupingOptions {
  RegroupingOnly = 'Regrouping Only',
  NoRegrouping = 'No Regrouping',
  Both = 'Both'
}

/**
 * Which cell of a math fact is left blank for the student to fill in.
 *
 * A setting on the Math Facts format rather than a format of its own: the
 * problem is byte-for-byte an existing fact, only a different cell is hidden.
 * `Mixed` alternates the two operands - never the answer, which would just be
 * the ordinary worksheet. See ADR 0005.
 */
export enum MissingNumberOptions {
  Off = 'Off',
  FirstNumber = 'First Number',
  SecondNumber = 'Second Number',
  Mixed = 'Mixed'
}

/**
 * Which stories a word-problem sheet draws (ADR 0073 section 2). Simple is every sheet as it
 * was before Varied existed, draw for draw. Varied adds the story types whose unknown is not
 * the result, and deals the operations evenly on every page.
 */
export enum StoryTypeOptions {
  Simple = 'Simple',
  Varied = 'Varied'
}

export enum ProblemFormatOptions {
  MathFacts = 'Math Facts',
  WordProblem = 'Word Problems',
  Fractions = 'Fractions',
  Money = 'Money',
  Decimals = 'Decimals',
  RoundingComparing = 'Rounding & Comparing',
  PlaceValue = 'Place Value',
  TellingTime = 'Telling Time',
  OrderOfOperations = 'Order of Operations',
  FactFamily = 'Fact Families',
  SpiralReview = 'Spiral Review',
  Algebra = 'Algebra',
  Integers = 'Integers',
  NumberProperties = 'Number Properties',
  UnitConversion = 'Unit Conversion',
  NumberPatterns = 'Number Patterns',
  NumberLine = 'Number Line',
  Percent = 'Percent',
  Ratio = 'Ratio',
  Shapes = 'Perimeter & Area',
  Volume = 'Volume',
  Circles = 'Circles',
  Probability = 'Probability',
  PythagoreanTheorem = 'Pythagorean Theorem',
  Averages = 'Averages',
  BarGraphs = 'Bar Graphs',
  Geometry = 'Geometry',
  ColorByNumber = 'Color by Number',
  RomanNumerals = 'Roman Numerals',
  LinePlots = 'Line Plots',
  CoordinatePlane = 'Coordinate Plane',
  RiddleDecoder = 'Riddle Decoder',
  MagicSquares = 'Magic Squares',
  Calendar = 'Calendar',
  Histograms = 'Histograms',
  BoxPlots = 'Box Plots'
}

/**
 * How an algebra question is asked (ADR 0028 §4).
 *
 * Solving reads a letter as a number you do not know yet; evaluating
 * substitutes one you were given. Both are 6.EE, and a mixed sheet asks a
 * student to notice which they are looking at.
 */
export enum AlgebraQuestionOptions {
  SolveOneStep = 'Solve for the Variable',
  SolveTwoStep = 'Two-Step Equations',
  // `5x - 3 = 2x + 9` (ADR 0138). A solving type like two-step, outside Mixed.
  VariablesOnBothSides = 'Variables on Both Sides',
  // `2x + 3y = 18` with `2x + y = 10`, answering (3, 4) (ADR 0139). Outside Mixed.
  SystemsOfEquations = 'Systems of Equations',
  Inequality = 'Inequalities',
  EvaluateExpression = 'Evaluate the Expression',
  // Expression questions (ADR 0119): the answer is an expression, not a number.
  // Outside Mixed, so every existing algebra sheet prints as it did.
  Distribute = 'Distributive Property',
  CombineLikeTerms = 'Combining Like Terms',
  Factor = 'Factoring',
  Equivalent = 'Equivalent Expressions',
  // Words to an expression (ADR 0123). Also outside Mixed and Equivalent.
  WritingExpressions = 'Writing Expressions',
  Mixed = 'Mixed'
}

/**
 * Largest value an algebra problem is *built from* - the solution, the
 * constant, the value the variable takes. Not every number that ends up
 * printed: `12k = 204` is a legitimate problem at a cap of 20, because the
 * answer it asks for is 17. ADR 0028 §4 records why capping the printed
 * numbers instead would leave multiplication with almost nothing to draw.
 */
export const ALGEBRA_MAX_VALUE_OPTIONS = [10, 12, 20, 50];

/**
 * Letters a variable may be. `l`, `o` and `i` are excluded: next to 1 and 0 in
 * Helvetica at the sizes these sheets print, they are a reading test rather
 * than a maths one (ADR 0028 §5).
 */
export const ALGEBRA_VARIABLES = ['x', 'n', 'a', 'k', 'm', 'y', 'b', 'c'];

/**
 * What an integers sheet asks (ADR 0030 §3).
 *
 * The operations are named here rather than taken from the operator bar, which
 * is why that bar is hidden for this format: `Adding & Subtracting` beside a
 * ticked multiplication button would be two controls disagreeing.
 */
export enum IntegerQuestionOptions {
  AddSubtract = 'Adding & Subtracting',
  MultiplyDivide = 'Multiplying & Dividing',
  // Comparing two integers, not ordering a list of them: an ordering answer is
  // a sequence, and every other answer this generator produces is one value or
  // one symbol. Plan 0030 called this "Comparing & Ordering"; it does not order.
  Compare = 'Comparing',
  AbsoluteValue = 'Absolute Value',
  // The rational types (ADR 0122) sit outside Mixed: `/integer-worksheets` says
  // "four kinds of problem" and "no fractions or decimals", and both stay true.
  RationalAddSubtract = 'Adding & Subtracting Rationals',
  RationalMultiplyDivide = 'Multiplying & Dividing Rationals',
  Rational = 'Mixed Rationals',
  Mixed = 'Mixed'
}

/** The three question types that ask about decimals and fractions (ADR 0122 §1). */
export const RATIONAL_QUESTIONS: ReadonlySet<IntegerQuestionOptions> = new Set([
  IntegerQuestionOptions.RationalAddSubtract,
  IntegerQuestionOptions.RationalMultiplyDivide,
  IntegerQuestionOptions.Rational,
]);

export function isRationalQuestion(question: IntegerQuestionOptions | undefined): boolean {
  return question !== undefined && RATIONAL_QUESTIONS.has(question);
}

/**
 * Which numbers a rational sheet is made of (ADR 0122 §2). Each problem is one
 * kind or the other, never `-0.5 + 3/4`; Both deals the two evenly.
 */
export enum RationalKindOptions {
  Decimals = 'Decimals',
  Fractions = 'Fractions',
  Both = 'Both',
}

/**
 * Largest magnitude an integer problem is built from - read the same way as
 * ALGEBRA_MAX_VALUE_OPTIONS above. A product of two of them is necessarily
 * larger than either.
 */
export const INTEGER_MAX_VALUE_OPTIONS = [10, 12, 20, 50];

/**
 * What a number-properties sheet asks (ADR 0032 §1).
 *
 * Even & Odd sits here rather than in a format of its own: it is a
 * divisibility question, taught in the unit that teaches factors, and as a
 * format it would have one question and nothing to vary.
 */
export enum NumberPropertyOptions {
  Factors = 'Factors',
  Multiples = 'Multiples',
  GCF = 'Greatest Common Factor',
  LCM = 'Least Common Multiple',
  EvenOdd = 'Even & Odd',
  Powers = 'Squares & Cubes',
  // ADR 0132. Single types outside Mixed and FactorsAndMultiples: a sheet of
  // roots is what a teacher searching "square roots" wants, and existing
  // sheets print exactly as before.
  SquareRoots = 'Square Roots',
  CubeRoots = 'Cube Roots',
  EstimatingRoots = 'Estimating Square Roots',
  // ADR 0136. Another single type outside Mixed and FactorsAndMultiples: the product,
  // quotient and power-of-a-power rules of integer exponents, one sheet that mixes them.
  ExponentRules = 'Exponent Rules',
  PrimeComposite = 'Prime & Composite',
  // A scoped mix, not a seventh sibling of the rest (ADR 0041 §3): factors,
  // multiples, GCF and LCM are one curriculum unit taught together, and this
  // is the format's second Mixed the way `TimeQuestionOptions.MixedElapsed`
  // is Time's - a real curriculum seam earns a narrower option beside the
  // full one, not a preference for a shorter list.
  FactorsAndMultiples = 'Factors & Multiples',
  Mixed = 'Mixed'
}

const ROOT_QUESTIONS: ReadonlySet<NumberPropertyOptions> = new Set([
  NumberPropertyOptions.SquareRoots,
  NumberPropertyOptions.CubeRoots,
  NumberPropertyOptions.EstimatingRoots,
]);

/**
 * The three root types (ADR 0132). Their radicands come from a fixed list of
 * roots, so the largest-number dial does not apply to them.
 */
export function isRootQuestion(question: NumberPropertyOptions | undefined): boolean {
  return question !== undefined && ROOT_QUESTIONS.has(question);
}

/**
 * The question types whose numbers come from a fixed list rather than the
 * largest-number dial: the three roots (ADR 0132) and the exponent rules (ADR 0136).
 */
export function ignoresNumberPropertyMaxValue(question: NumberPropertyOptions | undefined): boolean {
  return isRootQuestion(question) || question === NumberPropertyOptions.ExponentRules;
}

/** Largest number a property question is asked about. */
export const NUMBER_PROPERTY_MAX_VALUE_OPTIONS = [20, 50, 100, 144];

/**
 * The exponents that print as plain characters, and why (ADR 0037 §2).
 *
 * `²` and `³` are in WinAnsi; `⁴` is not, and ADR 0014 §3 refuses to embed a
 * font - the PDF layer registers none at all. `4^3` is calculator notation on
 * a sheet a child writes on by hand, and "4 to the power of 3" is a sentence.
 * Any other exponent is a raised `sup` run drawn by `MathText` (ADR 0132), as
 * scientific notation (0130) and the exponent rules (0136) do.
 *
 * Powers keeps to squares and cubes, which is where the curriculum is: most
 * fourth powers are a calculator exercise.
 *
 * Used by both `OrderOfOperationsGenerator` and `NumberPropertyGenerator`.
 */
export const SUPERSCRIPT: Record<2 | 3, string> = { 2: "²", 3: "³" };

/**
 * Which unit system a conversion sheet draws from (ADR 0032 §2).
 *
 * There is no size dial to go with this: what makes a conversion hard is the
 * unit pair, not the number, and this setting already picks that.
 */
/**
 * What kind of sequence a patterns sheet draws (ADR 0032 §5).
 *
 * Skip counting is the multiples of one number and is the third-grade version;
 * arithmetic adds a starting point and a direction; geometric multiplies.
 */
/**
 * Which number system a number line asks about (ADR 0033 §2).
 *
 * All three ask the same question - what number is the marker on - which is
 * what lets one drawing serve them all.
 */
/** A rectangle to draw, and the labels on it. Built by ShapeGenerator. */
export interface RectangleSpec {
  width: number;
  height: number;
  /** Absent when the question hides that side. */
  widthLabel?: string;
  heightLabel?: string;
}

/** What a Scale Drawing problem prints besides its rectangle (ADR 0107 §2, §3). */
export interface ScaleDrawingSpec {
  /** Metres of ground per centimetre of drawing. */
  scale: number;
  /** The drawing's sides in cm; the same numbers as the rectangle's. */
  width: number;
  height: number;
  /** The side labelled "?" in Drawing length. */
  hidden?: 'width' | 'height';
  /** The second scale of Another scale, in metres per cm. */
  newScale?: number;
  /** The line printed under the drawing, in the sheet's language: "Scale: 1 cm = 4 m". */
  line: string;
}

/**
 * A parallelogram, trapezoid or triangle standing on a horizontal base, with its
 * height drawn as a dashed line (ADR 0121 §2). Built by ShapeGenerator.
 *
 * Corners, with the base along y = 0 and `h` the height:
 * parallelogram (0,0) (base,0) (offset+base,h) (offset,h);
 * trapezoid (0,0) (base,0) (offset+top,h) (offset,h);
 * triangle (0,0) (base,0) (offset,h).
 * The height drops from (offset,h), so its foot (offset,0) is inside the base.
 * The offset is drawn but never printed.
 */
export interface AreaFigureSpec {
  kind: 'parallelogram' | 'trapezoid' | 'triangle';
  /** The bottom side, along a horizontal line. */
  base: number;
  /** Trapezoid only: the top parallel side, shorter than the base. */
  top?: number;
  height: number;
  /** How far right the top's left end (or the apex) sits from the base's left end. */
  offset: number;
}

/**
 * What a perimeter-and-area sheet asks (ADR 0038 §1).
 *
 * Rectangles, and from ADR 0121 the three figures the sixth-grade area
 * formulas are for (parallelogram, trapezoid, triangle). Compound shapes are
 * out with reasons.
 */
export enum ShapeQuestionOptions {
  Perimeter = 'Perimeter',
  Area = 'Area',
  MissingSide = 'Missing Side',
  /** 7.G.A.1, ADR 0107. Outside Mixed, so existing sheets are unchanged. */
  ScaleDrawing = 'Scale Drawing',
  /** 6.G.A.1, ADR 0121. These four are outside Mixed too. */
  Parallelogram = 'Parallelogram Area',
  Trapezoid = 'Trapezoid Area',
  Triangle = 'Triangle Area',
  /** The three above, dealt evenly on every page. */
  Polygons = 'Polygon Area',
  Mixed = 'Mixed'
}

/** The four questions that draw a parallelogram, trapezoid or triangle (ADR 0121). */
export function isAreaFigureQuestion(question: ShapeQuestionOptions): boolean {
  return (
    question === ShapeQuestionOptions.Parallelogram ||
    question === ShapeQuestionOptions.Trapezoid ||
    question === ShapeQuestionOptions.Triangle ||
    question === ShapeQuestionOptions.Polygons
  );
}

/** The largest side a rectangle may have. */
export const SHAPE_MAX_SIDE_OPTIONS = [12, 20, 50, 100];

/**
 * A box to draw, and the labels on it. Built by VolumeGenerator (ADR 0095 §2).
 *
 * `length`, `width` and `height` set the drawn proportions of the front face
 * (length by height); `width` is the depth, drawn at the fixed cabinet slope.
 * `cubes` asks for the unit-cube grid on the three visible faces instead of
 * labels.
 */
export interface BoxSpec {
  length: number;
  width: number;
  height: number;
  /** Absent when the question hides that edge, or draws cubes instead. */
  lengthLabel?: string;
  widthLabel?: string;
  heightLabel?: string;
  cubes?: boolean;
}

/**
 * Two boxes side by side with one depth, drawn as a step (ADR 0097 §2).
 * All five edges are labelled; VolumeGenerator keeps the heights at least 2
 * apart and the whole solid within 4:1 (ADR 0097 §4).
 */
export interface JoinedBoxesSpec {
  depth: number;
  left: { width: number; height: number };
  right: { width: number; height: number };
}

/**
 * What a volume sheet asks (ADR 0095 §3).
 *
 * Counting is the 5.MD.C.3-4 question and the other two are 5.MD.C.5.
 */
export enum VolumeQuestionOptions {
  CountCubes = 'Count the cubes',
  EdgeLengths = 'Edge lengths',
  MissingEdge = 'Missing edge',
  /** 5.MD.C.5c, ADR 0097. Outside Mixed, so existing sheets are unchanged. */
  TwoBoxes = 'Two boxes',
  /** 6.G.A.4 and 7.G.B.6, ADR 0100. Outside Mixed, so existing sheets are unchanged. */
  SurfaceArea = 'Surface Area',
  /** 8.G.C.9, ADR 0140. Outside Mixed (eighth grade), so existing sheets are unchanged. */
  Cylinders = 'Cylinders',
  Cones = 'Cones',
  Spheres = 'Spheres',
  /** The three solids dealt evenly over each page (ADR 0140 §1). */
  Solids = 'Cylinders, cones and spheres',
  Mixed = 'Mixed'
}

/** The question types about a cylinder, cone or sphere (ADR 0140), for the panel's dial label and the generator. */
export const SOLID_VOLUME_QUESTIONS: VolumeQuestionOptions[] = [
  VolumeQuestionOptions.Cylinders,
  VolumeQuestionOptions.Cones,
  VolumeQuestionOptions.Spheres,
  VolumeQuestionOptions.Solids
];

/** A round solid a volume problem draws (ADR 0140). */
export type SolidKind = 'cylinder' | 'cone' | 'sphere';

/**
 * The round solid to draw (ADR 0140 §5): its whole-number radius and height in
 * cm (a sphere has no height), and whether the label gives the radius or the
 * diameter, which is `2 * radius`. The volume stays in `display`.
 */
export interface SolidSpec {
  kind: SolidKind;
  radius: number;
  height?: number;
  given: 'radius' | 'diameter';
}

/** The longest edge a labelled box may have (ADR 0095 §4). */
export const VOLUME_MAX_EDGE_OPTIONS = [5, 10, 12];

/**
 * A circle to draw, built by CircleGenerator (ADR 0106 §2): the one labelled
 * segment through it, and how long it says it is. `length` is whole
 * centimetres, or `null` for a diameter the student is to find, labelled `?`.
 * `radius` is the radius the circle was built from, kept so the generator can
 * tell two problems apart (and keep neighbours different) whichever way round
 * the drawing labels it; the renderer does not read it.
 */
export interface CircleSpec {
  given: 'radius' | 'diameter';
  length: number | null;
  radius: number;
}

/**
 * What a circles sheet asks (ADR 0106 §3), all with 3.14 for pi. The
 * "relationship" half of 7.G.B.4 is the third: the circumference back to the
 * diameter.
 */
export enum CircleQuestionOptions {
  Circumference = 'Circumference',
  Area = 'Area',
  DiameterFromCircumference = 'Diameter from Circumference',
  Mixed = 'Mixed'
}

/** The largest radius a circle may have (ADR 0106 §1). */
export const CIRCLE_MAX_RADIUS_OPTIONS = [5, 10, 20];

/**
 * What a probability sheet asks (ADR 0108 §2). Mixed deals the other three
 * evenly.
 */
export enum ProbabilityQuestionOptions {
  OneSpinner = 'One Spinner',
  BagOfCounters = 'Bag of Counters',
  TwoEvents = 'Two Events',
  Mixed = 'Mixed'
}

/**
 * The four colours a bag of counters holds, named for the English letter that
 * labels each. Only the generator and the prompts use these: the language
 * decides the letter a colour is printed with (ADR 0108 §1), so the data
 * carries the printed letters, not these.
 */
export type CounterColour = 'R' | 'B' | 'G' | 'Y';

/**
 * What a probability problem draws and counted (ADR 0108 §1, §2).
 *
 * `spinners` is one spinner's section labels (1 to 9, repeats allowed) or two
 * spinners', left then right. `counters` is how many of each colour the bag
 * holds, keyed by the letter printed on those counters in the sheet's language
 * and in the order they were dealt; a colour that is absent is left out.
 * `counterKey` is the line printed under the bag. `favourable` and `total` are
 * the outcomes the event covers and all the outcomes, before simplifying, so
 * the generator can be held to asking for answers that need simplifying; a
 * count question has `total` and no `favourable`. The renderer reads neither.
 * `event` names the event and is what tells two problems apart.
 */
export interface ProbabilityData {
  spinners?: number[][];
  counters?: Record<string, number>;
  counterKey?: string;
  event: string;
  favourable?: number;
  total: number;
}

/**
 * What a Pythagorean theorem sheet asks (ADR 0109 §2). Mixed deals the other
 * three evenly.
 */
export enum PythagoreanQuestionOptions {
  Hypotenuse = 'Hypotenuse',
  MissingLeg = 'Missing Leg',
  RightTriangle = 'Is It a Right Triangle?',
  Mixed = 'Mixed'
}

/** The longest side (the hypotenuse) a Pythagorean problem may have (ADR 0109 §2). */
export const PYTHAGOREAN_MAX_SIDE_OPTIONS = [25, 50, 100];

/** The corner a right angle sits in: bottom-left, bottom-right, top-right, top-left. */
export type RightTriangleCorner = 0 | 1 | 2 | 3;

/**
 * A right triangle to draw (ADR 0109 §3), in whole cm. `a` is the horizontal
 * leg and `b` the vertical one, in either order of size; `c` is the
 * hypotenuse. `missing` names the side labelled `?`. `corner` is where the
 * right angle sits, so the hypotenuse is not always the slanted side on the right.
 */
export interface RightTriangleSpec {
  a: number;
  b: number;
  c: number;
  missing?: 'a' | 'b' | 'c';
  corner: RightTriangleCorner;
}

/**
 * What a Pythagorean problem was built from (ADR 0109 §4). `triple` is the
 * scaled Pythagorean triple (short leg, long leg, hypotenuse) every question is
 * built on, kept so neighbouring problems can be kept apart. A converse
 * problem also records the three `sides` it printed, in increasing order, and
 * whether they `isRight`.
 */
export interface PythagoreanData {
  triple: [number, number, number];
  sides?: [number, number, number];
  isRight?: boolean;
}

/**
 * What an averages sheet asks (ADR 0039 §2).
 *
 * Mean is the constrained one - the list is built so it comes out whole, on
 * the standing rule that a fractional answer belongs to Decimals, not to
 * whatever format happened to produce one (ADR 0032 §4, ADR 0034 §3). Median,
 * mode and range need no such build-up, but each has its own rule: an odd
 * list length so the median is always a member of the list, and exactly one
 * mode so the key never has to print "none" or pick between two answers.
 */
export enum AverageQuestionOptions {
  Mean = 'Mean',
  Median = 'Median',
  Mode = 'Mode',
  Range = 'Range',
  Mixed = 'Mixed'
}

/**
 * How many values an averages list holds. Odd only - [5, 7, 9] has no even
 * entry - so a median is always one of the listed values rather than the
 * average of the two middle ones (ADR 0039 §2).
 */
export const AVERAGE_LIST_LENGTH_OPTIONS = [5, 7, 9];

/**
 * What a bar-graph sheet asks (ADR 0039 §3). Three of the four are arithmetic
 * with the operands drawn off a chart instead of written; ReadOne is the only
 * one that is actually a reading question.
 */
export enum BarGraphQuestionOptions {
  ReadOne = 'Reading a Bar',
  Difference = 'Comparing Two Bars',
  Total = 'Adding the Bars',
  Largest = 'Finding the Largest',
  Mixed = 'Mixed'
}

/** The top gridline a bar chart is scaled to. */
export const BAR_GRAPH_MAX_OPTIONS = [10, 20, 50, 100];

/**
 * One bar graph: the bars to draw and the scale to draw them at. Every value
 * is a multiple of `gridStep`, so every bar ends exactly on a labelled
 * gridline (ADR 0039 §3) - a value a student would have to estimate has no
 * single right answer.
 */
export interface BarChartSpec {
  bars: { label: string; value: number }[];
  maxValue: number;
  gridStep: number;
}

/**
 * How many questions a single chart carries (ADR 0040 §3-4). Every problem in
 * the group carries the same chart, drawn once above the group's rows rather
 * than once per question - so raising this amortises one drawing over more
 * questions instead of drawing more charts. Four is the most a chart supports
 * before its questions start repeating themselves: with five or six
 * categories there are only so many pairs worth comparing.
 */
export const BAR_GRAPH_QUESTIONS_PER_CHART_OPTIONS = [2, 3, 4];

/**
 * Which problems share one rendered lead-in, and what that lead-in is
 * (ADR 0040 §1). `GeneratePage` groups the page's problems on `key` and
 * renders whichever of `heading` / `barChart` the tag carries once above the
 * group's rows, laid out by `format`.
 *
 * One mechanism with two lead-ins, not two mechanisms (ADR 0031's lesson):
 * spiral review (ADR 0013) sets `heading`; bar graphs (ADR 0040) sets
 * `barChart`. Nothing sets both, but nothing stops a third lead-in from being
 * added here later rather than as a parallel grouping field.
 */
export interface ProblemGroup {
  /** Problems sharing this value render together, under one lead-in. */
  key: string;
  /** Which renderer lays out the group's rows. */
  format: ProblemFormatOptions;
  /** Spiral review's lead-in: the heading printed above the section. */
  heading?: string;
  /** A bar graph's lead-in: the chart every problem in the group asks about. */
  barChart?: BarChartSpec;
  /** A line plot's lead-in: the title and plot every problem in the group asks about (ADR 0084 §7). */
  linePlot?: LinePlotSpec;
  /** A histogram's lead-in: the title and bars every problem in the group asks about (ADR 0099 §1). */
  histogram?: HistogramSpec;
  /** A box plot's lead-in: the title and five-number summary every problem in the group asks about (ADR 0099 §1). */
  boxPlot?: BoxPlotSpec;
  /**
   * A coordinate plane's grid, drawn beside the group's questions rather than
   * above them (ADR 0085 §1).
   */
  coordinateGrid?: CoordinateGridSpec;
  /** A month, drawn once above the page's questions (ADR 0093 §2). */
  calendar?: CalendarSpec;
}

/**
 * What a calendar sheet asks (ADR 0093 §4). `Reading` is the first-grade set
 * and never counts days; `Mixed` asks all four.
 */
export enum CalendarQuestionOptions {
  DayOfWeek = 'Day of the Week',
  FindDate = 'Find the Date',
  HowMany = 'How Many',
  BeforeAfter = 'Days Before and After',
  Reading = 'Reading the Calendar',
  Mixed = 'Mixed'
}

/**
 * How far a before-and-after question counts, and the format's difficulty
 * dial (ADR 0093 §5). Easiest first: Differentiation.ts steps along this order.
 */
export enum CalendarSpanOptions {
  OneDay = 'One Day',
  DaysAndWeeks = 'Days and Weeks',
  UpToTwentyDays = 'Up to 20 Days'
}

/**
 * Which month a calendar sheet draws (ADR 0093 §6): any month, or one named
 * month in a different year of the window on each page. In calendar order, so
 * a month's index in this list is its number.
 */
export enum CalendarMonthOptions {
  Any = 'Any Month',
  January = 'January',
  February = 'February',
  March = 'March',
  April = 'April',
  May = 'May',
  June = 'June',
  July = 'July',
  August = 'August',
  September = 'September',
  October = 'October',
  November = 'November',
  December = 'December'
}

/**
 * One month to draw (ADR 0093 §3): its title ("October 2027"), the seven
 * column headings Sunday first, the weekday of the 1st (0 is Sunday) and how
 * many days it has. The strings are the printed language's, computed once by
 * the generator, as a line plot's labels are.
 */
export interface CalendarSpec {
  title: string;
  weekdays: string[];
  firstWeekday: number;
  days: number;
}

/**
 * What a coordinate-plane sheet asks (ADR 0085 §2). Polygons draws a shape
 * instead of loose points and asks about it (ADR 0087 §1), and Triangles draws
 * a triangle and asks its base, height and area (ADR 0092); MissingCorner gives
 * three corners of a rectangle as pairs and asks the fourth (ADR 0094);
 * PointDistance asks how far apart two points are that share no coordinate
 * (ADR 0125); SlopeFromGraph and SlopeFromPoints (ADR 0126) ask for the slope of
 * a drawn line and of the line through two pairs. Mixed leaves all six out.
 */
export enum CoordinateQuestionOptions {
  ReadPoint = 'Read a Point',
  NamePoint = 'Name a Point',
  Distance = 'Distance',
  Quadrant = 'Quadrant',
  Mixed = 'Mixed',
  Polygons = 'Polygons',
  Triangles = 'Triangles',
  MissingCorner = 'Missing Corner',
  PointDistance = 'Distance Between Points',
  SlopeFromGraph = 'Slope from a Graph',
  SlopeFromPoints = 'Slope from Two Points'
}

/** The grid a coordinate-plane sheet draws, and its difficulty dial (ADR 0085 §4). Easiest first. */
export enum CoordinateGridOptions {
  FirstQuadrant = 'First Quadrant',
  FourQuadrants = 'Four Quadrants'
}

/**
 * One coordinate grid (ADR 0085 §3): both axes run from `min` to `max`, every
 * line labelled, and each point sits on a crossing with its letter beside it.
 */
export interface CoordinateGridSpec {
  min: number;
  max: number;
  points: { label: string; x: number; y: number }[];
  /** The points, in label order, are the corners of a closed shape, drawn (ADR 0087 §1). */
  outline?: boolean;
}

/**
 * What a line-plot sheet asks (ADR 0084 §5). `Mixed` is the reading and
 * comparing set for grades 2 to 5; `CenterAndSpread` is the sixth-grade
 * summary set. Median is in only the second, so a second-grade sheet never
 * asks for one.
 */
export enum LinePlotQuestionOptions {
  ReadOne = 'How Many at a Value',
  Difference = 'How Many More',
  Total = 'How Many in All',
  MostCommon = 'Most Common Value',
  Range = 'Range',
  Median = 'Median',
  Mixed = 'Mixed',
  CenterAndSpread = 'Median, Mode and Range'
}

/**
 * The unit between a line plot's ticks, and its difficulty dial (ADR 0084 §4).
 * In order, coarsest first: Differentiation.ts steps along this order.
 */
export enum LinePlotScaleOptions {
  Whole = 'Whole Numbers',
  Halves = 'Halves',
  Quarters = 'Quarters',
  Eighths = 'Eighths'
}

/**
 * The most marks one tick of a line plot carries, and the rows every plot
 * leaves room for, so plots on one sheet are one height (ADR 0084 §2-§3).
 */
export const LINE_PLOT_MAX_STACK = 6;

/** The mark stacked above each value (ADR 0084 §6). */
export enum LinePlotMarkOptions {
  X = 'X',
  Dot = 'Dot'
}

/**
 * One line plot (ADR 0084 §2-§3): a title naming the data, one label per
 * tick, and how many marks stand above each tick. `labels` are the exact
 * strings the prompts and the answer key use, computed once by the
 * generator, so the drawing and the words can never disagree.
 */
export interface LinePlotSpec {
  title: string;
  labels: string[];
  counts: number[];
  mark: LinePlotMarkOptions;
}

/**
 * What a histogram sheet asks (ADR 0099 §2). Never a single value, the median,
 * the mean or the range: a bar covers an interval, so a histogram doesn't show
 * them.
 */
export enum HistogramQuestionOptions {
  OneInterval = 'One Interval',
  AtLeast = 'At Least',
  Total = 'Total',
  Most = 'Most',
  Mixed = 'Mixed'
}

/**
 * One histogram (ADR 0099 §2): a title naming the data, one closed
 * whole-number interval label per bar (`70–79`), how many values fall in
 * each, and the interval each bar covers so the prompts can say "from 70 to
 * 79" and "80 or more" from the same numbers the drawing uses. `gridStep` is
 * 1 or 2 and every count is a multiple of it, so every bar ends on a gridline.
 */
export interface HistogramSpec {
  title: string;
  /** The bars' interval labels, lowest first. */
  labels: string[];
  /** The lowest whole number in each bar's interval. */
  lows: number[];
  /** The highest whole number in each bar's interval. */
  highs: number[];
  counts: number[];
  gridStep: number;
  /** The top of the y-axis, a multiple of `gridStep`. */
  maxValue: number;
}

/**
 * What a box-plot sheet asks (ADR 0099 §3). Reading a drawn plot only: making
 * one means finding quartiles, where the conventions disagree.
 */
export enum BoxPlotQuestionOptions {
  Median = 'Median',
  Quartile = 'Quartiles',
  Range = 'Range',
  InterquartileRange = 'Interquartile Range',
  Mixed = 'Mixed'
}

/**
 * One box plot (ADR 0099 §3): a title naming the data, the number line it is
 * drawn over (`from` to `to` by `step`, every tick labelled) and the five
 * numbers, all on ticks, strictly increasing.
 */
export interface BoxPlotSpec {
  title: string;
  from: number;
  to: number;
  step: number;
  min: number;
  q1: number;
  median: number;
  q3: number;
  max: number;
}

/**
 * What a ratio sheet asks (ADR 0035 §2).
 *
 * Only Simplify answers with a pair; the other three are the pair being used,
 * and answer with one value. Equivalent and Proportion are the same
 * mathematics in two notations, and both ship on purpose.
 */
export enum RatioQuestionOptions {
  Simplify = 'Simplifying',
  Equivalent = 'Equivalent Ratios',
  UnitRate = 'Unit Rate',
  Proportion = 'Proportions',
  /** 7.RP.A.2, ADR 0104. Outside Mixed, so existing sheets are unchanged. */
  Proportional = 'Proportional Tables',
  /** 7.RP.A.2, ADR 0105. Outside Mixed too. */
  ProportionalGraphs = 'Proportional Graphs',
  Mixed = 'Mixed'
}

/** The largest value a ratio is built from. */
export const RATIO_MAX_VALUE_OPTIONS = [20, 50, 100, 144];

/**
 * What a geometry sheet asks (ADR 0049 §1).
 *
 * All five are sentences with a number in them. The figures are described in
 * words and never drawn - see ADR 0049 §2 for why that is the design rather
 * than a shortcut, §3 for why there is no protractor question, and §4 for why
 * nothing is classified by eye.
 */
export enum GeometryQuestionOptions {
  ClassifyAngle = 'Acute, Right or Obtuse',
  MissingAngle = 'Missing Angle',
  ComplementSupplement = 'Complementary & Supplementary',
  ClassifyTriangle = 'Classifying Triangles',
  PolygonSides = 'Naming Polygons',
  Mixed = 'Mixed'
}

/**
 * How round the angles are: every angle a sheet prints is a multiple of this.
 *
 * Ordered easiest first, which is what `Differentiation.ts` steps along
 * (ADR 0049 §6) - `180 - 130` at ten, `180 - 137` at one. The question type is
 * deliberately not the difficulty axis; classifying an angle is not easier
 * than finding a complement, just different.
 *
 * Every value divides both 90 and 180, so a right angle and a straight angle
 * are reachable at every step. `ClassifyAngle` depends on that.
 */
export const GeometryAngleStepOptions = [10, 5, 1];

/**
 * Polygons by side count, for `PolygonSides` (ADR 0049 §1).
 *
 * Stops at ten. Beyond a decagon the names stop being the ones a curriculum
 * teaches and start being the ones a quiz show asks.
 */
export const POLYGONS: { name: string; sides: number }[] = [
  { name: 'triangle', sides: 3 },
  { name: 'quadrilateral', sides: 4 },
  { name: 'pentagon', sides: 5 },
  { name: 'hexagon', sides: 6 },
  { name: 'heptagon', sides: 7 },
  { name: 'octagon', sides: 8 },
  { name: 'nonagon', sides: 9 },
  { name: 'decagon', sides: 10 },
];

/**
 * What a percent sheet asks (ADR 0034 §2).
 *
 * Percent is notation over values that already exist, so these are all
 * conversions or ordinary whole-number arithmetic - there is no percent type.
 */
export enum PercentQuestionOptions {
  Convert = 'Converting',
  PercentOf = 'Percent of a Number',
  FindThePercent = 'Finding the Percent',
  PercentChange = 'Percent of Change',
  // Money questions (ADR 0120): each deals its contexts evenly, and none is
  // in Mixed, so every sheet made before they existed prints as it did.
  TaxAndTip = 'Tax and Tip',
  DiscountAndMarkup = 'Discount and Markup',
  SimpleInterest = 'Simple Interest',
  PercentWordProblems = 'Percent Word Problems',
  Mixed = 'Mixed'
}

/** The five situations the money questions are asked in (ADR 0120 §1). */
export type PercentContext = 'tax' | 'tip' | 'discount' | 'markup' | 'interest';

/** The members that print money in a sentence, and so have their own per-page counts. */
export const MONEY_PERCENT_QUESTIONS: ReadonlySet<PercentQuestionOptions> = new Set([
  PercentQuestionOptions.TaxAndTip,
  PercentQuestionOptions.DiscountAndMarkup,
  PercentQuestionOptions.SimpleInterest,
  PercentQuestionOptions.PercentWordProblems,
]);

/** The largest base a percent question is asked about. */
export const PERCENT_MAX_VALUE_OPTIONS = [50, 100, 200, 500];

/** What a number line drawing needs. Built by NumberLineGenerator. */
export interface NumberLineSpec {
  from: number;
  to: number;
  intervals: number;
  labelEvery: number;
  markAt: number;
  /** Set when the ticks should be labelled as fractions. */
  denominator?: number;
}

export enum NumberLineQuestionOptions {
  WholeNumbers = 'Whole Numbers',
  Integers = 'Integers',
  Fractions = 'Fractions',
  Mixed = 'Mixed'
}

/** The largest labelled value on a number line. */
export const NUMBER_LINE_RANGE_OPTIONS = [10, 20, 50, 100];

export enum PatternQuestionOptions {
  SkipCounting = 'Skip Counting',
  Arithmetic = 'Adding & Subtracting',
  Geometric = 'Doubling & Tripling',
  Mixed = 'Mixed'
}

/** How many terms a sequence shows, including the missing one. */
export const PATTERN_LENGTH_OPTIONS = [5, 6, 8];

/**
 * Which way a Roman numeral question runs (ADR 0079 §2). Mixed draws each
 * problem as one or the other.
 */
export enum RomanQuestionOptions {
  ToRoman = 'Number to Roman',
  ToNumber = 'Roman to Number',
  Mixed = 'Mixed'
}

/**
 * The largest value a Roman numeral sheet asks about (ADR 0079 §4). 3999 is
 * the ceiling: MMMM is not standard, and a numeral past it needs a bar over a
 * letter, which the page cannot print.
 */
export const ROMAN_MAX_OPTIONS = [10, 20, 100, 1000, 3999];

/**
 * The largest number in a magic square: the format's one difficulty dial
 * (ADR 0088 §4). Nine is the classic square, 1 to 9 with a total of 15.
 */
export const MAGIC_SQUARE_MAX_OPTIONS = [9, 20, 50, 100];

export enum ConversionQuestionOptions {
  Metric = 'Metric',
  Customary = 'Customary',
  Time = 'Time',
  Mixed = 'Mixed'
}

/**
 * The formats a spiral-review sheet may put in a section (ADR 0013 §3). Word
 * problems (a paragraph) and long division (a Math-Facts-only bracket layout)
 * are excluded on purpose. Telling time is allowed but capped at one section.
 */
export const SPIRAL_SECTION_FORMATS: ProblemFormatOptions[] = [
  ProblemFormatOptions.MathFacts,
  ProblemFormatOptions.Fractions,
  ProblemFormatOptions.Money,
  ProblemFormatOptions.Decimals,
  ProblemFormatOptions.PlaceValue,
  ProblemFormatOptions.RoundingComparing,
  ProblemFormatOptions.TellingTime,
];

/** The most sections a spiral-review sheet holds - four fits one page (§4). */
export const MAX_SPIRAL_SECTIONS = 4;

/**
 * How a fact family is asked. `FourEquations` gives the three numbers and four
 * blank equations; `NumberBond` draws the whole-and-parts diagram with one
 * circle empty; `MissingPart` states the relationship in words. See ADR 0020 §2.
 */
export enum FactFamilyOptions {
  FourEquations = 'Write the four facts',
  NumberBond = 'Number bond',
  MissingPart = 'Find the missing part'
}

/**
 * One fact family: two parts, their whole, and which operation relates them.
 * A multiplicative family is built from the two factors so the product always
 * divides evenly. Its own field, never a fourth `numbers` entry. See ADR 0020.
 */
export interface FactFamily {
  parts: [number, number];
  whole: number;
  multiplicative: boolean;
  kind: FactFamilyOptions;
  /** Which value is blank on a number-bond or missing-part problem. */
  blank: 'part0' | 'part1' | 'whole' | null;
}

/**
 * How hard an order-of-operations expression is. Spans grades 4 to 6, so it is
 * a difficulty dial rather than a single sheet. See ADR 0019 §4.
 */
export enum OrderOfOperationsLevelOptions {
  TwoOperations = 'Two operations',
  ThreeOperations = 'Three operations',
  Parentheses = 'With parentheses',
  Exponents = 'With exponents'
}

/**
 * Whether the operator bar means anything for a format. It is hidden for the
 * one-number formats (they have no operator) and for Order of Operations, whose
 * operators are a property of the expression rather than something to select -
 * `isArithmetic` stays true there because the format does have operands. ADR 0019 §1.
 */
export function showsOperatorBar(format: ProblemFormatOptions): boolean {
  return (
    isArithmetic(format) &&
    format !== ProblemFormatOptions.OrderOfOperations &&
    format !== ProblemFormatOptions.Integers &&
    format !== ProblemFormatOptions.NumberProperties &&
    format !== ProblemFormatOptions.UnitConversion &&
    format !== ProblemFormatOptions.Percent &&
    format !== ProblemFormatOptions.Ratio &&
    format !== ProblemFormatOptions.Shapes &&
    format !== ProblemFormatOptions.Volume &&
    format !== ProblemFormatOptions.Circles
  );
}

/**
 * Whether a sheet asks a student to round a number, compare two of them, or
 * both. One format with a sub-setting, following Place Value's three question
 * types rather than splitting into two formats. See ADR 0006.
 */
export enum RoundingComparingOptions {
  Rounding = 'Rounding',
  Comparing = 'Comparing',
  Mixed = 'Mixed'
}

/**
 * The place a number is rounded to. The whole-number places apply when the
 * sheet is whole numbers; the tenth/whole places apply when it is decimals.
 * `Mixed` picks from whichever set fits the sheet.
 */
export enum RoundingPlaceOptions {
  Ten = 'Nearest 10',
  Hundred = 'Nearest 100',
  Thousand = 'Nearest 1,000',
  Tenth = 'Nearest tenth',
  Whole = 'Nearest whole',
  Mixed = 'Mixed'
}

/**
 * How a rounding or comparing question is shown - the plain sentence, or a
 * number line marking the number(s) involved. Applies to both of the
 * format's questions, subject to the availability rule in ADR 0046 §2: a
 * rounding line needs the number precise to one place finer than the
 * rounding place, so nearest hundred and thousand never get one and fall
 * back to Plain regardless of this setting.
 */
export enum RoundingDisplayOptions {
  Plain = 'Plain',
  NumberLine = 'A number line',
}

/** How many digits a whole number to round or compare has. */
export const RoundingComparingDigitOptions = [2, 3, 4, 5];

/** Decimal places for a rounding/comparing sheet; 0 means whole numbers. */
export const RoundingComparingDecimalOptions = [0, 1, 2];

/** How many decimal places a decimal problem may use, at most. */
export const DecimalPlacesOptions = [1, 2, 3];

/**
 * Largest whole-number part a decimal problem may use.
 *
 * 100 at three places is the widest thing this format prints ("+ 100.000").
 * Checked against the rendered PDF at every page count, full and half: it fits.
 */
export const DecimalMaxWholeOptions = [10, 20, 100];

/**
 * Formats that ask about a single number rather than combining two of them.
 *
 * These have no operator and no operands, so the operator selection means
 * nothing to them: the generator answers them before it looks at it, and the
 * operator bar is hidden on their sheets.
 */
export const NonArithmeticFormats: ProblemFormatOptions[] = [
  ProblemFormatOptions.RoundingComparing,
  ProblemFormatOptions.PlaceValue,
  ProblemFormatOptions.TellingTime,
  // A sequence has no operands and no operator - the step is a property of the
  // pattern, not something the sheet applies to two numbers (ADR 0032 §5).
  ProblemFormatOptions.NumberPatterns,
  // Nor does a number line: it is a drawing with a question about it.
  ProblemFormatOptions.NumberLine,
  // Averages ask about a whole list, not a pair of operands - there is
  // nothing for an operator to sit between (ADR 0039 §1).
  ProblemFormatOptions.Averages,
  // A bar graph has a scale, not two operands with an operator between them -
  // the question type names what to do with the chart (ADR 0039 §3).
  ProblemFormatOptions.BarGraphs,
  // A geometry question is a sentence about a figure described in words - an
  // angle measure, three side lengths, the name of a polygon. There are no
  // operands for an operator to sit between (ADR 0049 §1).
  ProblemFormatOptions.Geometry,
  // A numeral is converted, not combined with another: no operands (ADR 0079 §1).
  ProblemFormatOptions.RomanNumerals,
  // A line plot is a data set drawn on a number line; the question type says
  // what to do with it, as a bar graph's does (ADR 0084 §1).
  ProblemFormatOptions.LinePlots,
  // A grid of labelled points, asked about; nothing is combined (ADR 0085 §1).
  ProblemFormatOptions.CoordinatePlane,
  // A magic square is a grid with a total, not an operand pair: nothing for an
  // operator to sit between (ADR 0088 §4). A riddle decoder is not here - it
  // asks the arithmetic generator for plain facts, as color by number does.
  ProblemFormatOptions.MagicSquares,
  // A month with questions about it; nothing is combined (ADR 0093 §1).
  ProblemFormatOptions.Calendar,
  // A drawing of a data set with questions about it; nothing is combined
  // (ADR 0099 §1).
  ProblemFormatOptions.Histograms,
  ProblemFormatOptions.BoxPlots,
  // A spinner or a bag of counters with a question about it; nothing is
  // combined (ADR 0108 §1).
  ProblemFormatOptions.Probability,
  // A right triangle, or three lengths, with a question about it; nothing is
  // combined (ADR 0109 §2).
  ProblemFormatOptions.PythagoreanTheorem,
];

export function isArithmetic(format: ProblemFormatOptions): boolean {
  return !NonArithmeticFormats.includes(format);
}

/**
 * Which way round a clock question is asked. Reading hands off a face and
 * drawing them onto a blank one are different skills, and students who can do
 * the first often cannot do the second.
 */
export enum TimeQuestionOptions {
  ReadTheClock = 'Read the Clock',
  DrawTheHands = 'Draw the Hands',
  Mixed = 'Mixed',
  /** start + end → duration */
  HowMuchTime = 'How much time passed',
  /** start + duration → end */
  WhatTimeEnds = 'What time does it end',
  /** end − duration → start */
  WhatTimeStarted = 'What time started',
  /** the three elapsed-time questions together; `Mixed` stays read-and-draw */
  MixedElapsed = 'Mixed elapsed',
  /**
   * An event and a printed time: circle a.m. or p.m. (ADR 0090 §1). In
   * neither `Mixed` nor `MixedElapsed`.
   */
  AmOrPm = 'A.M. or P.M.',
}

/** The three elapsed-time question types, and the mix of them. */
export const ELAPSED_TIME_QUESTIONS: TimeQuestionOptions[] = [
  TimeQuestionOptions.HowMuchTime,
  TimeQuestionOptions.WhatTimeEnds,
  TimeQuestionOptions.WhatTimeStarted,
  TimeQuestionOptions.MixedElapsed,
];

export function isElapsedTimeQuestion(question: TimeQuestionOptions): boolean {
  return ELAPSED_TIME_QUESTIONS.includes(question);
}

/** How the two times in an elapsed-time problem are shown. */
export enum TimeDisplayOptions {
  TwoClocks = 'Two clock faces',
  TwoDigital = 'Two digital times',
  ClockAndDigital = 'One of each',
  /** A word-problem sentence rather than clocks or bare times. */
  Sentence = 'A sentence',
  /** A number line marking whichever times the question gives (ADR 0044). */
  NumberLine = 'A number line',
}

/**
 * How precise the times are, in the order telling the time is taught. The
 * minute value is what each step rounds to.
 */
export enum TimeIntervalOptions {
  Hour = 'Hour',
  HalfHour = 'Half Hour',
  QuarterHour = 'Quarter Hour',
  FiveMinutes = 'Five Minutes',
  Minute = 'Minute'
}

/** Minutes each interval steps by. */
export const TimeIntervalMinutes: Record<TimeIntervalOptions, number> = {
  [TimeIntervalOptions.Hour]: 60,
  [TimeIntervalOptions.HalfHour]: 30,
  [TimeIntervalOptions.QuarterHour]: 15,
  [TimeIntervalOptions.FiveMinutes]: 5,
  [TimeIntervalOptions.Minute]: 1,
};

/**
 * Which way round a place value question is asked. Reading a number and
 * writing it out is a different skill from being handed the parts and
 * assembling them, and naming what one digit is worth is a third.
 */
export enum PlaceValueOptions {
  ExpandedForm = 'Expanded Form',
  StandardForm = 'Standard Form',
  DigitValue = 'Digit Value',
  Mixed = 'Mixed',
  // ADR 0130. Outside Mixed, so the older sheets print as they did.
  ScientificNotation = 'Scientific Notation',
  ScientificOperations = 'Scientific Notation: Multiply & Divide'
}

/**
 * The two scientific-notation types (ADR 0130). Their numbers come from a fixed
 * recipe, so the digits control does not apply to them.
 */
export function isScientificQuestion(question: PlaceValueOptions | undefined): boolean {
  return (
    question === PlaceValueOptions.ScientificNotation ||
    question === PlaceValueOptions.ScientificOperations
  );
}

/**
 * A scientific-notation problem's numbers, all held as scaled integers (ADR 0130
 * §2): a coefficient in hundredths (`452` is 4.52) and each exponent as an integer.
 */
export type ScientificProblem =
  | {
      kind: 'convert';
      /** True when the sheet shows standard form and asks for scientific notation. */
      toScientific: boolean;
      large: boolean;
      /** The coefficient in hundredths, 100 to 999, with no trailing zero. */
      coefficient: number;
      /** How many significant digits it has: 1, 2 or 3. */
      significant: 1 | 2 | 3;
      exponent: number;
    }
  | {
      kind: 'operate';
      operation: 'multiply' | 'divide';
      /** The two numbers in the prompt, each a coefficient in hundredths, and their exponents. */
      first: number;
      second: number;
      firstExponent: number;
      secondExponent: number;
      /** The first step's coefficient in hundredths, and its exponent. */
      stepCoefficient: number;
      stepExponent: number;
      /** True when the key shows a second step, renormalising the first. */
      renormalised: boolean;
      /** The answer's coefficient in hundredths and its exponent. */
      answerCoefficient: number;
      answerExponent: number;
    };

/**
 * An exponent-rules problem's numbers (ADR 0136 §2): one base and two exponents,
 * and the single power `base^k` the rule gives. `product` is `a^m x a^n`
 * (k = m + n), `quotient` is `a^m / a^n` (k = m - n), `power` is `(a^m)^n` (k = m * n).
 */
export type ExponentRuleProblem = {
  rule: 'product' | 'quotient' | 'power';
  base: number;
  m: number;
  n: number;
  k: number;
};

/** How many digits a place value number has. */
export const PlaceValueDigitOptions = [2, 3, 4, 5];

/** Names of the columns, smallest first. */
export const PlaceNames = ['ones', 'tens', 'hundreds', 'thousands', 'ten thousands'];

/**
 * Whether an addition or subtraction problem uses two fractions over the same
 * denominator. Unlike denominators are the harder skill - the one that needs a
 * common denominator found first - so they are worth practising on their own.
 */
export enum FractionDenominatorOptions {
  Like = 'Like Denominators',
  Unlike = 'Unlike Denominators',
  Both = 'Both'
}

/** Largest denominator a generated fraction may use. */
export const MaxDenominatorOptions = [6, 8, 10, 12];

/**
 * What a fraction sheet asks. `Arithmetic` is the shipped behaviour and stays
 * the default, so every existing fraction route and link is unchanged. Each of
 * the others has a degenerate draw that the generator must exclude. See ADR 0021.
 *
 * `NameTheModel` and `CompareModels` are the two visual-model question types
 * (ADR 0043 §1): a picture is the prompt rather than the decoration, which is
 * why they are new question types on this format rather than a presentation
 * toggle on the existing ones - see ADR 0043's Alternatives for why a toggle
 * was rejected.
 */
export enum FractionQuestionOptions {
  Arithmetic = 'Add, subtract, multiply, divide',
  Simplify = 'Simplify',
  Equivalent = 'Equivalent fractions',
  Compare = 'Compare',
  MixedAndImproper = 'Mixed & improper numbers',
  FractionOfANumber = 'Fraction of a number',
  ToDecimal = 'Fractions to decimals',
  NameTheModel = 'Fractions from a picture',
  CompareModels = 'Comparing pictures',
  // ADR 0045: two scoped mixes, not one mix of all nine - the notation
  // sub-skills and the drawn models do not share a page shape (ADR 0045 §1),
  // so each mix expands only within its own group. Appended rather than
  // interleaved: order is serialized into URLs (ADR 0009) and pinned by
  // routes (ADR 0045 §Out of scope).
  MixedSkills = 'Mixed fraction skills',
  MixedModels = 'Mixed picture questions'
}

export function isFractionSubskill(question: FractionQuestionOptions): boolean {
  return question !== undefined && question !== FractionQuestionOptions.Arithmetic;
}

/**
 * Either of the two visual-model question types (ADR 0043 §1), or the mix
 * over them (ADR 0045 §2). `MixedModels` must answer true here: every feature
 * keyed on this predicate - the differentiation step, the per-page arrays,
 * the settings panel's denominator control - has to treat it exactly like
 * `NameTheModel` and `CompareModels`, the same hazard ADR 0043 recorded.
 */
export function isFractionModelQuestion(question: FractionQuestionOptions): boolean {
  return (
    question === FractionQuestionOptions.NameTheModel ||
    question === FractionQuestionOptions.CompareModels ||
    question === FractionQuestionOptions.MixedModels
  );
}

/**
 * Largest denominator a fraction-model drawing may use (ADR 0043 §3).
 *
 * Bounded separately from `MaxDenominatorOptions`, and lower: a bar cut into
 * sixteenths at worksheet width has parts about four points wide, and a
 * student counting them will miscount - a drawing that is unambiguous only to
 * someone with a ruler is ambiguous. Twelve is the largest that stays
 * comfortably countable, and it happens to cover the denominators curricula
 * actually use for models.
 */
export const FractionModelMaxDenominatorOptions = [4, 6, 8, 10, 12];

/**
 * Largest money amount a problem may use, in dollars. Under a dollar keeps
 * everything in cents, which is where money practice starts.
 */
export const MoneyRangeOptions = [
  { label: 'Under $1', maxDollars: 1 },
  { label: 'Under $10', maxDollars: 10 },
  { label: 'Under $100', maxDollars: 100 },
];

/**
 * What a money sheet asks. `Arithmetic` is the shipped behaviour and stays the
 * default, so every existing money route and link is unchanged. See ADR 0018.
 */
export enum MoneyQuestionOptions {
  Arithmetic = 'Adding & subtracting',
  CountingCoins = 'Counting coins',
  MakingChange = 'Making change',
  Mixed = 'Mixed',
  /** Arithmetic amounts told as a story about spending or saving. ADR 0063. */
  WordProblems = 'Word problems',
}

export function isCoinMoneyQuestion(question: MoneyQuestionOptions): boolean {
  return (
    question === MoneyQuestionOptions.CountingCoins ||
    question === MoneyQuestionOptions.MakingChange ||
    question === MoneyQuestionOptions.Mixed
  );
}

/**
 * US coins, value in cents, with a diameter for drawing (mm, used only for
 * relative size). The dime being smaller than the nickel and the penny is not a
 * typo; it is the fact children have to learn. See ADR 0018 §2.
 */
export const Coins: { name: string; cents: number; diameter: number }[] = [
  { name: 'penny', cents: 1, diameter: 19.05 },
  { name: 'nickel', cents: 5, diameter: 21.21 },
  { name: 'dime', cents: 10, diameter: 17.91 },
  { name: 'quarter', cents: 25, diameter: 24.26 },
  { name: 'half dollar', cents: 50, diameter: 30.61 },
  { name: 'dollar', cents: 100, diameter: 26.49 },
];

/** The coin names on by default: half dollar and dollar coin are unfamiliar. */
export const DEFAULT_MONEY_COINS = ['penny', 'nickel', 'dime', 'quarter'];

export interface CoinProblem {
  kind: 'counting' | 'change';
  /** The coins drawn, by name, for a counting problem. */
  coins: string[];
  /** The answer as an amount ("$0.67"): the coin total, or the change. */
  answerText: string;
  /** Making change: the sticker price and the amount handed over. */
  priceText?: string;
  paidText?: string;
}